import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger
} from '../../DropdownMenu';
import { ChevronDown, Close } from '../../../icons';
import styles from './multiSelect.module.scss';
import selectStyles from '../Select/select.module.scss';
import Chip from '../../Chip';
import { cx } from 'class-variance-authority';
import { useMemo } from 'react';

type MultiSelectItem<T> = {
  label: string;
  value: T;
};

type MultiSelectProps<TValue> = {
  items: MultiSelectItem<TValue>[];
  selectedItems: MultiSelectItem<TValue>[];
  setSelectedItems: (items: MultiSelectItem<TValue>[]) => void;
  placeholder?: string;
};

export function MultiSelectItem<T>({
  isSelected,
  label,
  value,
  onSelect
}: MultiSelectItem<T> &
  Readonly<{
    isSelected: boolean;
    onSelect: (item: MultiSelectItem<T>) => void;
  }>) {
  return (
    <DropdownMenuCheckboxItem
      className={styles.item}
      onSelect={(e) => {
        e.preventDefault();
        onSelect({ label, value });
      }}
      key={value as string}
      checked={isSelected}
    >
      {label}
    </DropdownMenuCheckboxItem>
  );
}

export default function MultiSelect<TItemValue>({
  items,
  selectedItems,
  setSelectedItems,
  placeholder = 'Select'
}: Readonly<MultiSelectProps<TItemValue>>) {
  const selectedItemsByValue = useMemo(
    () =>
      selectedItems.reduce(
        (selectedItems, item) => {
          selectedItems[item.value as string] = item;
          return selectedItems;
        },
        {} as Record<string, MultiSelectItem<TItemValue>>
      ),
    [selectedItems]
  );

  const updateSelectedItems = (newItem: MultiSelectItem<TItemValue>) => {
    if (selectedItemsByValue[newItem.value as string]) {
      setSelectedItems(
        selectedItems.filter((item) => item.value !== newItem.value)
      );
    } else {
      setSelectedItems([...selectedItems, newItem]);
    }
  };

  return (
    <DropdownMenu>
      <div className={styles.multiSelect}>
        <DropdownMenuTrigger
          className={cx(selectStyles.selectTrigger, styles.trigger)}
        >
          <div className={cx(styles.selectedItems, styles.selectedItemsHidden)}>
            {selectedItems.length === 0
              ? placeholder
              : selectedItems.map((selectedItem) => (
                  <Chip
                    key={selectedItem.value as string}
                    className={styles.chip}
                  >
                    <div>{selectedItem.label}</div>
                    <Close />
                  </Chip>
                ))}
          </div>
          <ChevronDown />
        </DropdownMenuTrigger>
        <div className={styles.selectedItemsOverlay}>
          <div className={styles.selectedItems}>
            {selectedItems.length === 0
              ? null
              : selectedItems.map((selectedItem) => (
                  <button
                    className={styles.chipButton}
                    aria-label={`Remove ${selectedItem.label}`}
                    onClick={() => updateSelectedItems(selectedItem)}
                    key={selectedItem.value as string}
                  >
                    <Chip className={styles.chip}>
                      <div>{selectedItem.label}</div>
                      <Close />
                    </Chip>
                  </button>
                ))}
          </div>
          <ChevronDown className={styles.overlayChevron} />
        </div>
        <DropdownMenuContent align="start">
          <DropdownMenuGroup>
            {items.map((item) => {
              const isSelected = !!selectedItemsByValue[item.value as string];
              return (
                <MultiSelectItem
                  key={item.value as string}
                  isSelected={isSelected}
                  label={item.label}
                  value={item.value}
                  onSelect={(selectedItem) => {
                    updateSelectedItems(selectedItem);
                  }}
                />
              );
            })}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
}
